// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import { handleApiCall, handleApiCallDeep } from "../utils/apiHelper";

const buildLlmDateParams = ({
  days,
  tenantId,
  dateFilter,
  startDate,
  endDate,
} = {}) => {
  const params = {};
  if (dateFilter) {
    if (dateFilter === "today") {
      params.days = 1;
    } else if (
      typeof dateFilter === "string" &&
      dateFilter.endsWith("days") &&
      /^(\d+)days$/.test(dateFilter)
    ) {
      params.days = parseInt(dateFilter, 10);
    } else if (dateFilter === "custom" && startDate && endDate) {
      params.date_filter = dateFilter;
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.date_filter = dateFilter;
    }
  } else if (days != null) {
    params.days = days;
  }
  if (tenantId) params.tenant_id = tenantId;
  return params;
};

const llmUsageService = {
  getDailyMetrics: async (
    days = null,
    tenantId = null,
    dateFilter = null,
    startDate = null,
    endDate = null,
  ) =>
    handleApiCallDeep(
      () =>
        apiClient.get("/llm-usage/daily-metrics", {
          params: buildLlmDateParams({
            days,
            tenantId,
            dateFilter,
            startDate,
            endDate,
          }),
        }),
      "Error fetching daily metrics",
    ),

  getUsageSummary: async (
    days = 7,
    tenantId = null,
    dateFilter = null,
    startDate = null,
    endDate = null,
  ) => {
    const params = {};
    if (dateFilter) {
      params.date_filter = dateFilter;
      if (dateFilter === "custom" && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
    } else if (days != null) {
      params.days = days;
    }
    if (tenantId) params.tenant_id = tenantId;
    return handleApiCallDeep(
      () => apiClient.get("/llm-usage/usage-summary", { params }),
      "Error fetching usage summary",
    );
  },

  getPerformanceMetrics: async (
    days = null,
    tenantId = null,
    dateFilter = null,
    startDate = null,
    endDate = null,
  ) =>
    handleApiCallDeep(
      () =>
        apiClient.get("/llm-usage/performance-metrics", {
          params: buildLlmDateParams({
            days,
            tenantId,
            dateFilter,
            startDate,
            endDate,
          }),
        }),
      "Error fetching performance metrics",
    ),

  getPerformanceToday: async (
    days = null,
    tenantId = null,
    dateFilter = null,
    startDate = null,
    endDate = null,
  ) =>
    handleApiCallDeep(
      () =>
        apiClient.get("/llm-usage/performance-today", {
          params: buildLlmDateParams({
            days,
            tenantId,
            dateFilter,
            startDate,
            endDate,
          }),
        }),
      "Error fetching performance today",
    ),

  getRegelwerkDistribution: async (
    days = null,
    tenantId = null,
    dateFilter = null,
    startDate = null,
    endDate = null,
  ) =>
    handleApiCallDeep(
      () =>
        apiClient.get("/llm-usage/regelwerk-distribution", {
          params: buildLlmDateParams({
            days,
            tenantId,
            dateFilter,
            startDate,
            endDate,
          }),
        }),
      "Error fetching regelwerk distribution",
    ),

  getLLMRuns: async (params = {}) => {
    const {
      tenant_id,
      status,
      run_type,
      limit = 20,
      offset = 0,
      date_filter,
      start_date,
      end_date,
    } = params;

    const queryParams = { limit, offset };
    if (tenant_id) queryParams.tenant_id = tenant_id;
    if (status) queryParams.status = status;
    if (run_type) queryParams.run_type = run_type;
    if (date_filter) {
      queryParams.date_filter = date_filter;
      if (date_filter === "custom" && start_date && end_date) {
        queryParams.start_date = start_date;
        queryParams.end_date = end_date;
      }
    }

    return handleApiCall(
      () => apiClient.get("/llm/llm-runs", { params: queryParams }),
      "Error fetching LLM runs",
    );
  },

  getApiPerformance: async (params = {}) => {
    const {
      days = 1,
      tenant_id,
      limit,
      offset,
      startDate,
      endDate,
      start_date,
      end_date,
    } = params;
    const queryParams = {};

    if (startDate || start_date) {
      queryParams.start_date = startDate || start_date;
    }
    if (endDate || end_date) {
      queryParams.end_date = endDate || end_date;
    }
    if (!queryParams.start_date && !queryParams.end_date) {
      queryParams.days = days;
    }
    if (tenant_id) queryParams.tenant_id = tenant_id;
    if (limit) queryParams.limit = limit;
    if (offset !== undefined) queryParams.offset = offset;

    return handleApiCall(
      () =>
        apiClient.get("/admin/workflow-api-calls/performance", {
          params: queryParams,
        }),
      "Error fetching API performance data",
    );
  },

  getWorkflowExecutionMetrics: async (params = {}) => {
    const { days = 1, tenant_id, limit, offset, start_date, end_date } = params;
    const queryParams = { days };
    if (tenant_id) queryParams.tenant_id = tenant_id;
    if (limit) queryParams.limit = limit;
    if (offset !== undefined) queryParams.offset = offset;
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;

    return handleApiCall(
      () =>
        apiClient.get("/admin/workflow-api-calls/execution-metrics-by-type", {
          params: queryParams,
        }),
      "Error fetching workflow execution metrics",
    );
  },

  getDashboardSummary: async ({
    tenantId = null,
    dateFilter,
    startDate,
    endDate,
  } = {}) => {
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (dateFilter) params.dateFilter = dateFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return handleApiCall(
      () =>
        apiClient.get("/admin/workflow-api-calls/dashboard-summary", {
          params,
        }),
      "Error fetching dashboard summary",
    );
  },
};

export default llmUsageService;
